import { memo, useCallback, useEffect } from "react";

import type { DashActionSurface } from "@mirage/analytics/events/enums/dash_action_surface";
import { MultiAnswerCard } from "@mirage/mosaics/MultiAnswersCard/MultiAnswerCard";
import {
  useAnswerConversationProps,
  useInitialConversation,
} from "@mirage/mosaics/MultiAnswersCard/useInitialConversation";
import useMultiAnswers from "@mirage/mosaics/MultiAnswersCard/useMultiAnswers";
import type { Connectors } from "@mirage/service-connectors/service";
import { useFeatureFlagValue } from "@mirage/service-experimentation/useFeatureFlagValue";
import { convertFeatureValueToBool } from "@mirage/service-experimentation/util";
import type { QuestionAndAnswerResponse } from "@mirage/shared/answers/multi-answer";
import { SearchFilter } from "@mirage/shared/search/search-filters";
import type { FeedbackOptions } from "@mirage/shared/types";

// This file manages different implementations of the multi answer card based on feature flags

export interface UseMultiAnswerCardProps {
  connectors: Connectors;
  query: string;
  activeFilters?: SearchFilter[];
  onShowFeedbackSnackbar: () => void;
  onShowCopyMessageSnackbar: () => void;
  annotationMode?: boolean;
  onOpenAnnotationModal: (
    feedback: FeedbackOptions,
    answer: QuestionAndAnswerResponse,
  ) => void;
  // Passed through to useInitialConversation for logging
  previousPathSurface?: DashActionSurface;
}

export function useMultiAnswerCardRenderer(props: UseMultiAnswerCardProps) {
  const isConversationAssistantEnabled = convertFeatureValueToBool(
    useFeatureFlagValue("dash_2025_02_11_conversation_assistant"),
  );
  const multiAnswerCardRenderer = useCallback(
    (variant: "narrow" | "wide") => {
      if (isConversationAssistantEnabled) {
        return <AssistantEnabledMultiAnswerCard {...props} variant={variant} />;
      } else {
        return (
          <AssistantDisabledMultiAnswerCard {...props} variant={variant} />
        );
      }
    },
    [isConversationAssistantEnabled, props],
  );
  return {
    multiAnswerCardRenderer,
    isConversationAssistantEnabled,
  };
}

interface MultiAnswerCardProps extends UseMultiAnswerCardProps {
  variant: "narrow" | "wide";
}

export const AssistantEnabledMultiAnswerCard = memo(
  ({
    query,
    activeFilters,
    variant,
    previousPathSurface,
    ...props
  }: MultiAnswerCardProps) => {
    const initialConversation = useInitialConversation(
      query,
      true /* isEnabled */,
      true /* isSerpAnswerConversation */,
      previousPathSurface,
    );
    const conversationProps = useAnswerConversationProps(
      true /* isEnabled */,
      initialConversation,
    );
    return (
      <MultiAnswerCard
        {...props}
        query={query}
        activeFilters={activeFilters}
        multiAnswerResponse={undefined}
        conversationProps={conversationProps}
        loading={initialConversation.isWaitingForResponse}
        isStreamingEnabled
        variant={variant}
      />
    );
  },
);
AssistantEnabledMultiAnswerCard.displayName = "AssistantEnabledMultiAnswerCard";

export const AssistantDisabledMultiAnswerCard = memo(
  ({ query, variant, ...props }: MultiAnswerCardProps) => {
    const {
      answers,
      loading: loadingAnswers,
      fetch: handleFetchAnswers,
      isStreamingEnabled,
      answersStreaming,
    } = useMultiAnswers();

    useEffect(() => {
      if (!query) return;

      handleFetchAnswers(query);
    }, [query, handleFetchAnswers]);

    return (
      <MultiAnswerCard
        {...props}
        query={query}
        loading={loadingAnswers}
        multiAnswerResponse={answers}
        isStreamingEnabled={isStreamingEnabled}
        conversationProps={{
          isEnabled: false,
        }}
        variant={variant}
        answersStreaming={answersStreaming}
      />
    );
  },
);
AssistantDisabledMultiAnswerCard.displayName =
  "AssistantDisabledMultiAnswerCard";
